import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Star } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { AfterFormInfoSection } from "@/components/landing/AfterFormInfoSection";
import { HeroSection } from "@/components/landing/HeroSection";
import { PromoTintSection } from "@/components/landing/PromoTintSection";
import { TintComparisonSlider } from "@/components/landing/TintComparisonSlider";
import { TintQuoteSection } from "@/components/landing/TintQuoteSection";

const bookingUrl = "https://app.urable.com/virtual-shop/V3FI9WfFTIdLhSEPBFlp";
const callUrl = "tel:+17737040795";
const textUrl = "sms:+17737040795";
const mapsUrl = "https://www.google.com/maps/search/?api=1&query=9824+Industrial+Dr+Bridgeview+IL+60455";
const logoUrl = "https://customer-assets.emergentagent.com/job_d-detail-pro/artifacts/v73su4t7_IMG_0987.JPG";
const tintPromoImageUrl = "https://customer-assets.emergentagent.com/job_d-detail-pro/artifacts/6t2f906e_IMG_0779.jpeg";

// Recent work gallery images
const recentWorkImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_export-station/artifacts/olwqg98g_photo-1440_singular_display_fullPicture.jpeg",
    alt: "White BMW with premium ceramic tint installation at Detail With D shop",
    title: "BMW Ceramic Tint"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_export-station/artifacts/azvq9cgj_photo-473_singular_display_fullPicture.jpeg",
    alt: "Gray SUV tint installation in progress showing professional workmanship",
    title: "SUV Tint Installation"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_export-station/artifacts/c2pj0dsg_IMG_9715.jpeg",
    alt: "Dark Tesla Model 3 with completed window tint - stunning finish",
    title: "Tesla Model 3 Complete"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_export-station/artifacts/26r4u254_IMG_9623.jpeg",
    alt: "Silver Lexus with professional window tint - Chicago location",
    title: "Lexus Premium Tint"
  },
];
const googleReviewUrl = "https://www.google.com/search?q=Detail+With+D+Chicago+reviews";
const googleWordmarkColors = ["#4285F4", "#EA4335", "#FBBC05", "#4285F4", "#34A853", "#EA4335"];

const testimonials = [
  {
    name: "Marko",
    slug: "marko",
    date: "Feb 2026",
    quote: "Daniel explained the tint options clearly and my Tesla looks cleaner, cooler, and more comfortable every day.",
    recency: "Recently",
  },
  {
    name: "Carlos Abrego",
    slug: "carlos-abrego",
    date: "Jan 2026",
    quote: "The tint install changed the whole look of the car. Great communication, smooth process, and a really clean final result.",
    recency: "1 month ago",
  },
  {
    name: "Michael Nahm",
    slug: "michael-nahm",
    date: "Dec 2025",
    quote: "I went with ceramic tint and the workmanship was excellent. No shortcuts, no mess, and the finish came out exactly right.",
    recency: "2 months ago",
  },
  {
    name: "Miguel Perez",
    slug: "miguel-perez",
    date: "Oct 2025",
    quote: "The tint on my truck came out even, dark, and professional. Trustworthy people, fair communication, and consistent results.",
    recency: "5 months ago",
  },
  {
    name: "Jeffrey Garcia",
    slug: "jeffrey-garcia",
    date: "Oct 2025",
    quote: "Very approachable and friendly staff. The workmanship on the tint was excellent and I would definitely come back.",
    recency: "5 months ago",
  },
];

const faqs = [
  {
    question: "How much does car window tint cost in the Chicago area?",
    answer: "Pricing depends on the vehicle, the number of windows, and the film you choose. The tint quote form helps us give you the most accurate estimate quickly.",
  },
  {
    question: "What is included in a tint quote?",
    answer: "Your quote is based on the glass you want tinted, the film package you choose, whether old tint removal is needed, and your vehicle size and shape.",
  },
  {
    question: "Is ceramic tint worth it?",
    answer: "Yes. Ceramic tint is a strong choice if you want better heat rejection, excellent clarity, and more daily comfort without a metallic look.",
  },
  {
    question: "How long does window tint installation take?",
    answer: "Most tint installs can be completed in a few hours, though larger vehicles or full windshield add-ons may take longer depending on the package selected.",
  },
];

const trustBadges = ["Ceramic Tint", "Carbon Film", "IR Rejection", "UV 99%", "Clean Install"];

export default function LandingPage() {
  const reviewCards = useMemo(
    () => testimonials.map((testimonial) => ({
      ...testimonial,
      initials: testimonial.name
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase(),
    })),
    [],
  );

  return (
    <main className="relative text-white" data-testid="landing-page">
      <HeroSection bookingUrl={bookingUrl} logoUrl={logoUrl} trustBadges={trustBadges} />

      <PromoTintSection quoteUrl="#contact" tintImageUrl={tintPromoImageUrl} />

      {/* Interactive Tint Comparison Section */}
      <section className="border-t border-white/10 py-24 md:py-32" id="gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-12 space-y-4 text-center"
            initial={{ opacity: 0, y: 36 }}
            transition={{ type: "spring", stiffness: 95, damping: 18 }}
            viewport={{ once: true, amount: 0.2 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <p className="section-kicker text-sm text-blue-400" data-testid="gallery-section-kicker">
              Interactive Tint Comparison
            </p>
            <h2 className="text-4xl font-light leading-[0.95] sm:text-5xl" data-testid="gallery-section-heading">
              See the Exact Difference - Real Car Interior View
            </h2>
            <p className="mx-auto max-w-2xl text-sm leading-7 text-zinc-300 sm:text-base" data-testid="gallery-section-description">
              Experience how different tint levels look from the driver's seat. Drag the slider or select a percentage to see the real impact on visibility and privacy.
            </p>
          </motion.div>

          <TintComparisonSlider />
        </div>
      </section>

      {/* Recent Work Gallery Section */}
      <section className="border-t border-white/10 py-24 md:py-32" id="recent-work">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 36 }}
            transition={{ type: "spring", stiffness: 95, damping: 18 }}
            viewport={{ once: true, amount: 0.2 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <p className="section-kicker text-sm text-blue-400">Our Work</p>
            <h2 className="mt-4 text-4xl font-light leading-[0.95] sm:text-5xl text-white">
              Recent Tint Installations
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-zinc-300 sm:text-base">
              Professional ceramic and carbon tint installations on luxury vehicles in Bridgeview, IL.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {recentWorkImages.map((image, index) => (
              <motion.div
                key={image.url}
                className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-black/20"
                initial={{ opacity: 0, y: 30 }}
                transition={{ type: "spring", stiffness: 95, damping: 18, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="aspect-square">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-sm font-medium text-white">{image.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            transition={{ delay: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-base font-medium text-white transition-all hover:bg-blue-700 hover:scale-105"
            >
              Get Your Tint Quote
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-white/10 py-24 md:py-32" id="testimonials">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-col gap-5"
            initial={{ opacity: 0, y: 36 }}
            transition={{ type: "spring", stiffness: 95, damping: 18 }}
            viewport={{ once: true, amount: 0.18 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="max-w-2xl space-y-4">
              <p className="section-kicker text-sm text-blue-400" data-testid="testimonials-section-kicker">
                Trusted by Chicago tint customers
              </p>
              <h2 className="text-4xl font-light leading-[0.95] sm:text-5xl" data-testid="testimonials-section-heading">
                Google review proof for tint installs that look clean and feel worth booking.
              </h2>
              <p className="max-w-xl text-sm text-zinc-300 sm:text-base" data-testid="testimonials-section-description">
                These reviews focus on the same things new tint customers care about most: clean installs, strong communication, better comfort, and a finished look that transforms the vehicle.
              </p>
            </div>

            <motion.div
              className="rounded-[2rem] bg-white px-6 py-8 text-black shadow-[0_24px_80px_rgba(0,0,0,0.24)] sm:px-8"
              data-testid="reviews-summary-card"
              whileHover={{ y: -6 }}
            >
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center" data-testid="reviews-summary-main-row">
                  <p className="text-[4rem] font-semibold leading-none" data-testid="reviews-summary-rating-value">5.0</p>

                  <div className="space-y-2" data-testid="reviews-summary-volume">
                    <div className="flex items-center gap-1 text-[#fbbc05]" data-testid="reviews-summary-stars">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star key={index} className="h-8 w-8 fill-current" />
                      ))}
                    </div>

                    <p className="text-xl font-medium text-black" data-testid="reviews-summary-caption">
                      124 reviews on {" "}
                      <span className="inline-flex items-center gap-[1px] align-middle" data-testid="reviews-summary-google-wordmark">
                        {"Google".split("").map((letter, index) => (
                          <span key={`${letter}-${index}`} style={{ color: googleWordmarkColors[index] }}>
                            {letter}
                          </span>
                        ))}
                      </span>
                    </p>
                  </div>
                </div>

                <a
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#ff3636] px-6 py-4 text-lg font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#ff2626] lg:w-auto"
                  data-testid="review-us-on-google-button"
                  href={googleReviewUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Review us on Google
                </a>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="grid gap-6"
            data-testid="testimonials-review-stack"
            initial={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", stiffness: 95, damping: 18, delay: 0.08 }}
            viewport={{ once: true, amount: 0.1 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            {reviewCards.map((testimonial) => {
              const truncatedQuote = testimonial.quote.length > 108
                ? `${testimonial.quote.slice(0, 108).trim()}...`
                : testimonial.quote;

              return (
                <article
                  key={testimonial.slug}
                  className="rounded-[2rem] bg-white px-6 py-7 text-black shadow-[0_18px_48px_rgba(0,0,0,0.2)] sm:px-8"
                  data-testid={`testimonial-card-${testimonial.slug}`}
                >
                  <div className="flex justify-center gap-1 text-[#fbbc05]" data-testid={`testimonial-stars-${testimonial.slug}`}>
                    {Array.from({ length: 5 }).map((_, starIndex) => (
                      <Star key={starIndex} className="h-8 w-8 fill-current" />
                    ))}
                  </div>

                  <p
                    className="mx-auto mt-5 max-w-4xl text-center text-[1.05rem] font-medium leading-8 text-black sm:text-[1.2rem]"
                    data-testid={`testimonial-quote-${testimonial.slug}`}
                  >
                    {truncatedQuote}
                  </p>

                  <button
                    className="mx-auto mt-3 block text-center text-[1.7rem] font-semibold text-[#ff3636] transition-colors duration-300 hover:text-[#ff2626]"
                    data-testid={`testimonial-read-more-${testimonial.slug}`}
                    type="button"
                  >
                    Read more
                  </button>

                  <div className="mt-6 flex flex-col items-center gap-3" data-testid={`testimonial-author-block-${testimonial.slug}`}>
                    <div className="relative" data-testid={`testimonial-avatar-wrapper-${testimonial.slug}`}>
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-zinc-900 text-2xl font-semibold text-white" data-testid={`testimonial-avatar-${testimonial.slug}`}>
                        {testimonial.initials}
                      </div>
                      <div className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md" data-testid={`testimonial-google-badge-${testimonial.slug}`}>
                        <span className="inline-flex items-center gap-[0.5px] text-sm font-semibold leading-none">
                          {"G".split("").map((letter, index) => (
                            <span key={`${testimonial.slug}-${letter}-${index}`} style={{ color: googleWordmarkColors[index] }}>
                              {letter}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-2" data-testid={`testimonial-author-row-${testimonial.slug}`}>
                      <p className="text-[1.15rem] font-semibold text-black sm:text-[1.35rem]">{testimonial.name}</p>
                      <BadgeCheck className="h-5 w-5 fill-[#ff3636] text-[#ff3636]" data-testid={`testimonial-verified-badge-${testimonial.slug}`} />
                    </div>

                    <p className="text-lg text-zinc-500" data-testid={`testimonial-date-${testimonial.slug}`}>
                      {testimonial.recency}
                    </p>
                  </div>
                </article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="border-t border-white/10 pb-4 pt-24 md:py-32" id="areas">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div className="rounded-[2rem] bg-[#242424] p-4 shadow-[0_22px_60px_rgba(0,0,0,0.26)] sm:p-6" initial={{ opacity: 0, y: 36 }} transition={{ type: "spring", stiffness: 95, damping: 18 }} viewport={{ once: true, amount: 0.18 }} whileInView={{ opacity: 1, y: 0 }}>
            <a
              className="flex w-full items-center justify-center rounded-[1.35rem] border border-white/10 bg-[#3a3a3a] px-6 py-5 text-center text-[1.7rem] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors duration-300 hover:bg-[#454545] sm:text-[2.15rem]"
              data-testid="service-area-call-button"
              href={callUrl}
            >
              Call For A Tint Consultation
            </a>

            <div className="relative mt-6 overflow-hidden rounded-[1.6rem] bg-white" data-testid="service-area-map-frame">
              <a
                className="absolute left-4 top-4 z-10 inline-flex items-center justify-center rounded-[1rem] border border-[#dbe3ee] bg-white px-4 py-3 text-base font-semibold text-[#1a73e8] shadow-sm transition-transform duration-300 hover:-translate-y-0.5"
                data-testid="service-area-open-maps-link"
                href={mapsUrl}
                rel="noreferrer"
                target="_blank"
              >
                Open in Maps ↗
              </a>
              <iframe
                className="h-[360px] w-full sm:h-[460px]"
                data-testid="service-area-map"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=9824+Industrial+Dr+Bridgeview+IL+60455&output=embed"
                title="Detail With D service area map"
              />
            </div>

            <div className="px-2 pb-2 pt-8 sm:px-4" data-testid="service-area-copy-block">
              <p className="text-[clamp(2.9rem,8vw,4.6rem)] font-light leading-[0.92] text-white" data-testid="service-area-section-heading">
                In-Person Tint Consultations Welcome
              </p>
              <div className="mt-6 h-1 w-44 rounded-full bg-[#c71d3d]" data-testid="service-area-heading-divider" />

              <p className="mt-8 max-w-4xl text-base leading-8 text-zinc-200 sm:text-[1.1rem]" data-testid="service-area-section-description">
                Do you have questions about ceramic tint, carbon film, windshield tint, or the right percentage for your vehicle? We welcome anyone who wants an in-person consultation at our Chicago shop. Call ahead to schedule a time or stop by during business hours to talk through the best tint setup for your car, truck, or SUV.
              </p>

              <div className="mt-7 flex flex-col gap-3 text-zinc-300 sm:flex-row sm:flex-wrap sm:items-center" data-testid="service-area-location-details">
                <p className="text-base font-medium text-white" data-testid="service-area-contact-address">9824 Industrial Dr, Bridgeview, IL 60455</p>
                <span className="hidden text-zinc-500 sm:inline">•</span>
                <p className="text-base" data-testid="service-area-hours-copy">Call or text first if you want to confirm availability before stopping by.</p>
              </div>
            </div>
          </motion.div>

          <motion.div className="mt-8 md:mt-10" initial={{ opacity: 0, y: 36 }} transition={{ type: "spring", stiffness: 95, damping: 18, delay: 0.08 }} viewport={{ once: true, amount: 0.18 }} whileInView={{ opacity: 1, y: 0 }}>
            <div className="panel-surface luxury-outline p-6" data-testid="faq-panel">
              <p className="section-kicker text-sm text-blue-400" data-testid="faq-section-kicker">
                FAQ
              </p>
              <h3 className="mt-3 text-3xl font-medium leading-[1.02] sm:text-[2.15rem]" data-testid="faq-section-heading">
                Quick answers before you book.
              </h3>

              <Accordion className="mt-6" collapsible type="single">
                {faqs.map((item) => {
                  const slug = item.question.toLowerCase().replace(/[^a-z0-9]+/g, "-");

                  return (
                    <AccordionItem key={slug} value={slug} data-testid={`faq-item-${slug}`}>
                      <AccordionTrigger className="text-left text-base text-white hover:no-underline" data-testid={`faq-trigger-${slug}`}>
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-7 text-zinc-300" data-testid={`faq-content-${slug}`}>
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </motion.div>
        </div>
      </section>

      <TintQuoteSection />

      <AfterFormInfoSection callUrl={callUrl} logoUrl={logoUrl} textUrl={textUrl} />

      <div className="fixed inset-x-4 bottom-4 z-50 md:hidden" data-testid="mobile-sticky-cta-wrapper">
        <div className="panel-surface luxury-outline flex items-center gap-2 p-2" data-testid="mobile-sticky-cta-card">
          <a
            className="glass-button glass-button-primary flex-1 px-4 py-3 text-center text-xs font-semibold tracking-[0.18em] text-white transition-colors duration-300 hover:bg-blue-500"
            data-testid="mobile-book-now-link"
            href={bookingUrl}
            rel="noreferrer"
            target="_blank"
          >
            Book now
          </a>
          <a
            className="glass-button glass-button-secondary flex-1 px-4 py-3 text-center text-xs font-semibold tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white/10"
            data-testid="mobile-call-now-link"
            href={callUrl}
          >
            Call now
          </a>
        </div>
      </div>

      <footer className="border-t border-white/10 px-4 py-10 text-sm text-zinc-300 sm:px-6 lg:px-8" data-testid="landing-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-4" data-testid="footer-brand-group">
            <div className="footer-full-logo-shell max-w-[340px] sm:max-w-[380px]" data-testid="footer-full-logo-shell">
              <img
                alt="Detail With D Auto Detailing full logo"
                className="footer-full-logo-image"
                data-testid="footer-full-logo-image"
                loading="lazy"
                src={logoUrl}
              />
            </div>
            <p data-testid="footer-brand-line">Premium window tint installation in Bridgeview, IL with cleaner looks, cooler cabins, and professional results.</p>
          </div>

          <a className="glass-chip w-fit px-4 py-2 text-blue-300" data-testid="footer-text-link" href={textUrl}>
            Text for a quote
          </a>
        </div>
      </footer>
    </main>
  );
}